// TopicService (§6). Správa témat nad in-memory projektem + repository + audit +
// eventbus. Při vytvoření doplní GUID, datum, autora, výchozí workflow stav.
import { createTopic, nowISO } from "../core/model.js";
import { pushAudit } from "../core/audit.js";

export class TopicService {
  constructor({ project, repository, events, workflow, currentUser }) {
    this.project = project;
    this.repository = repository;
    this.events = events;
    this.workflow = workflow;
    this.currentUser = currentUser || (() => null);
  }

  _user() { const u = this.currentUser(); return (u && u.id) || u || null; }

  async createTopic(data = {}) {
    const topic = createTopic({
      ...data,
      createdBy: data.createdBy || this._user(),
      status: data.status || (this.workflow ? this.workflow.initialState : "new"),
    });
    topic.workflow = { state: topic.status };
    pushAudit(topic, { userId: this._user(), action: "Topic created", after: { title: topic.title, status: topic.status } });
    this.project.topics.push(topic);
    if (this.repository) await this.repository.saveTopic(topic);
    this.events && this.events.emit("bcf.topic.created", { topic });
    return topic;
  }

  getTopic(id) { return this.project.topics.find((t) => t.id === id) || null; }
  listTopics() { return this.project.topics.slice(); }

  async updateTopic(id, patch = {}) {
    const topic = this.getTopic(id);
    if (!topic) throw new Error("Téma nenalezeno: " + id);
    const tracked = ["title", "description", "type", "priority", "assignedTo", "dueDate", "labels", "stage"];
    for (const k of tracked) {
      if (k in patch && patch[k] !== topic[k]) {
        pushAudit(topic, { userId: this._user(), action: k + " changed", before: topic[k], after: patch[k] });
        topic[k] = patch[k];
      }
    }
    topic.modifiedAt = nowISO();
    topic.modifiedBy = this._user();
    if (this.repository) await this.repository.saveTopic(topic);
    this.events && this.events.emit("bcf.topic.updated", { topic });
    return topic;
  }

  async deleteTopic(id) {
    const idx = this.project.topics.findIndex((t) => t.id === id);
    if (idx < 0) return false;
    this.project.topics.splice(idx, 1);
    if (this.repository) await this.repository.deleteTopic(id);
    this.events && this.events.emit("bcf.topic.deleted", { topicId: id });
    return true;
  }

  // Filtrování (§27) — podle statusu/typu/priority/odpovědnosti/autora/termínu/labelu/IFC prvku/modelu.
  filterTopics(query = {}) {
    return this.project.topics.filter((t) => {
      if (query.status && t.status !== query.status) return false;
      if (query.type && t.type !== query.type) return false;
      if (query.priority && t.priority !== query.priority) return false;
      if (query.assignedTo && t.assignedTo !== query.assignedTo) return false;
      if (query.author && t.createdBy !== query.author) return false;
      if (query.label && !(t.labels || []).includes(query.label)) return false;
      if (query.dueBefore && !(t.dueDate && new Date(t.dueDate) <= new Date(query.dueBefore))) return false;
      if (query.globalId || query.modelId) {
        const refs = [].concat(...t.viewpoints.map((v) => v.selection || []));
        const hit = refs.some((r) => (!query.globalId || r.globalId === query.globalId) && (!query.modelId || r.modelId === query.modelId));
        if (!hit) return false;
      }
      return true;
    });
  }

  sortTopics(topics, { by = "createdAt", dir = "desc" } = {}) {
    const s = topics.slice().sort((a, b) => {
      const av = a[by], bv = b[by];
      if (av == null) return 1; if (bv == null) return -1;
      return av < bv ? -1 : av > bv ? 1 : 0;
    });
    return dir === "desc" ? s.reverse() : s;
  }
}
